import { PersonaConfig, PersonaOrder, OrderStatus } from "../types";

// Customers for the Tractor Supply (TSC) CX crew demo: every row in the TSC Demos
// Supabase seed (tsc_cx_customers + tsc_cx_order_details), one entry per person.
// Numbered in the test doc's order (its callers first, then the rest of the seed).
// The page shows customers, never test scenarios: a numbered scenario would suggest
// Riley is scripted per case.
//
// The crew reads these rows live, so keep this in step with the seed. Statuses are the
// post-reset values. Order ages are pinned in the seed (tsc_cx_age_minutes reads
// pin_age_minutes), so "placed 12 min ago" stays true however long after a reset.

export interface TscOrder {
  orderId: string;
  product: string;
  brand: string;
  category: string;
  /** Seed status: processing / released / shipped / delivered. */
  status: string;
  totalUsd: number;
  quantity: number;
  /** Minutes since the order was placed, as the crew sees it. */
  ageMinutes: number;
  fulfilment: string;
  payment: string;
}

export interface TscCustomer {
  /** Customer id in the seed. */
  id: string;
  /** "Customer N" on the cards. */
  num: number;
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  zip: string;
  city: string;
  state: string;
  neighborsClub: boolean;
  /** Neighbor's Club reward, same numbers as Supabase db/17 (Riley reads them after a password reset). */
  ncReward?: { usd: number; daysLeft: number };
  photo: string;
  orders: TscOrder[];
  // DemoCustomer fields the shared registry reads.
  loyaltyTier: string;
  ordersCount: number;
  crmNotes: string;
}

/** The SOP's cancel-on-the-call window. */
export const CANCEL_WINDOW_MINUTES = 59;

export const isWithinWindow = (o: TscOrder) => o.ageMinutes <= CANCEL_WINDOW_MINUTES && o.status !== "delivered" && o.status !== "shipped";

export function placedLabel(minutes: number): string {
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.round(minutes / 1440);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

const order = (o: TscOrder) => o;

const PEOPLE: Omit<TscCustomer, "loyaltyTier" | "ordersCount" | "crmNotes" | "photo">[] = [
  {
    id: "273023690", num: 1, name: "Emma Ward", firstName: "Emma", lastName: "Ward", email: "emma.ward@gmail.com", phone: "6155550101",
    zip: "37064", city: "Franklin", state: "TN", neighborsClub: false,
    orders: [
      order({ orderId: "1410145510", product: "4health Grain-Free High Prairie Dog Food 30 lb", brand: "4health", category: "Dog Food", status: "processing", totalUsd: 54.99, quantity: 1, ageMinutes: 12, fulfilment: "Ships from warehouse · UPS", payment: "Credit card" }),
    ],
  },
  {
    id: "516816845", num: 2, name: "Sarah Lopez", firstName: "Sarah", lastName: "Lopez", email: "sarah.lopez@gmail.com", phone: "6155550103",
    zip: "37027", city: "Brentwood", state: "TN", neighborsClub: false,
    orders: [
      order({ orderId: "1410143518", product: "Purina Pro Plan Indoor Cat Food 16 lb", brand: "Purina", category: "Cat Food", status: "processing", totalUsd: 36.99, quantity: 1, ageMinutes: 180, fulfilment: "Store order · Brentwood store", payment: "PayPal" }),
      order({ orderId: "1410141786", product: "Kaytee Clean & Cozy Small Pet Bedding 49.2 L", brand: "Kaytee", category: "Small Pet Bedding", status: "processing", totalUsd: 19.99, quantity: 1, ageMinutes: 300, fulfilment: "Store order · Brentwood store", payment: "Credit card" }),
    ],
  },
  {
    id: "785628916", num: 3, name: "Maria Santos", firstName: "Maria", lastName: "Santos", email: "maria.santos@yahoo.com", phone: "6155550111",
    zip: "37090", city: "Lebanon", state: "TN", neighborsClub: true, ncReward: { usd: 5, daysLeft: 6 },
    orders: [
      order({ orderId: "1410023887", product: "Producer's Pride 6 ft Wood Picnic Table", brand: "Producer's Pride", category: "Outdoor Furniture", status: "shipped", totalUsd: 279.99, quantity: 1, ageMinutes: 7200, fulfilment: "Shipped from warehouse · XPO freight", payment: "PayPal" }),
    ],
  },
  {
    id: "433915192", num: 4, name: "Rachel Kim", firstName: "Rachel", lastName: "Kim", email: "rachel.kim@yahoo.com", phone: "6155550109",
    zip: "37015", city: "Ashland City", state: "TN", neighborsClub: false,
    orders: [
      order({ orderId: "1409656449", product: "Fi Series 3 Smart Dog Collar", brand: "Fi", category: "Pet Tech", status: "delivered", totalUsd: 119.99, quantity: 1, ageMinutes: 28800, fulfilment: "Delivered from warehouse · UPS", payment: "Credit card" }),
    ],
  },
  {
    id: "325952057", num: 5, name: "Mike Chen", firstName: "Mike", lastName: "Chen", email: "mike.chen@gmail.com", phone: "6155550104",
    zip: "37211", city: "Nashville", state: "TN", neighborsClub: true, ncReward: { usd: 2, daysLeft: 4 },
    orders: [
      order({ orderId: "1410121185", product: "Blue Buffalo Homestyle Wet Dog Food 12 pk", brand: "Blue Buffalo", category: "Dog Food", status: "released", totalUsd: 28.49, quantity: 1, ageMinutes: 1440, fulfilment: "Ships from warehouse · UPS", payment: "Credit card" }),
      order({ orderId: "1410097467", product: "Bernzomatic Propane Torch Kit", brand: "Bernzomatic", category: "Tools", status: "shipped", totalUsd: 49.99, quantity: 1, ageMinutes: 2880, fulfilment: "Shipped from warehouse · FedEx · hazmat", payment: "Credit card" }),
    ],
  },
  {
    id: "399569218", num: 6, name: "David Brooks", firstName: "David", lastName: "Brooks", email: "david.brooks@gmail.com", phone: "6155550106",
    zip: "37174", city: "Spring Hill", state: "TN", neighborsClub: false,
    orders: [
      order({ orderId: "1410097364", product: "Furbo 360 Dog Camera", brand: "Furbo", category: "Pet Tech", status: "processing", totalUsd: 159.99, quantity: 1, ageMinutes: 2880, fulfilment: "Ships from vendor · UPS", payment: "Credit card" }),
      order({ orderId: "1410047638", product: "Halo GPS Dog Collar Series 4", brand: "Halo", category: "Pet Tech", status: "shipped", totalUsd: 129.99, quantity: 1, ageMinutes: 5760, fulfilment: "Shipped from vendor · UPS", payment: "PayPal" }),
    ],
  },
  {
    id: "401357266", num: 7, name: "Linda Garcia", firstName: "Linda", lastName: "Garcia", email: "linda.garcia@yahoo.com", phone: "6155550107",
    zip: "37122", city: "Mount Juliet", state: "TN", neighborsClub: true, ncReward: { usd: 10, daysLeft: 3 },
    orders: [
      order({ orderId: "1410072639", product: "Producer's Pride Defender Chicken Coop", brand: "Producer's Pride", category: "Poultry Housing", status: "processing", totalUsd: 549.99, quantity: 1, ageMinutes: 4320, fulfilment: "Ships from vendor · XPO freight", payment: "Credit card" }),
      order({ orderId: "1409925520", product: "Lifetime 8 x 10 ft Outdoor Storage Shed", brand: "Lifetime", category: "Sheds", status: "shipped", totalUsd: 1299.0, quantity: 1, ageMinutes: 12960, fulfilment: "Shipped from vendor · XPO freight", payment: "Credit card" }),
      order({ orderId: "1409802831", product: "Behlen Country 300 Gallon Poly Stock Tank", brand: "Behlen Country", category: "Livestock Tanks", status: "processing", totalUsd: 329.99, quantity: 1, ageMinutes: 20160, fulfilment: "Ships from vendor · XPO freight · past promised date", payment: "Credit card" }),
    ],
  },
  {
    id: "794672783", num: 8, name: "Tom Baker", firstName: "Tom", lastName: "Baker", email: "tom.baker@gmail.com", phone: "6155550108",
    zip: "37013", city: "Antioch", state: "TN", neighborsClub: false,
    orders: [
      order({ orderId: "1410139054", product: "Producer's Pride Wild Bird Feed 40 lb", brand: "Producer's Pride", category: "Bird Feed", status: "processing", totalUsd: 21.99, quantity: 1, ageMinutes: 360, fulfilment: "Store order · Lebanon store", payment: "TSC gift card" }),
    ],
  },
  {
    id: "802627905", num: 9, name: "John Miller", firstName: "John", lastName: "Miller", email: "john.miller@yahoo.com", phone: "6155550102",
    zip: "37203", city: "Nashville", state: "TN", neighborsClub: true, ncReward: { usd: 5, daysLeft: 5 },
    orders: [
      order({ orderId: "1410144918", product: "Retriever All Life Stages Dog Food 35 lb", brand: "Retriever", category: "Dog Food", status: "released", totalUsd: 52.99, quantity: 1, ageMinutes: 25, fulfilment: "Ships from warehouse · FedEx", payment: "TSC gift card" }),
    ],
  },
  {
    id: "858054517", num: 10, name: "Priya Patel", firstName: "Priya", lastName: "Patel", email: "priya.patel@yahoo.com", phone: "6155550105",
    zip: "37069", city: "Franklin", state: "TN", neighborsClub: false,
    orders: [
      order({ orderId: "1410125878", product: "Producer's Pride Poultry Feeder and Waterer Set", brand: "Producer's Pride", category: "Poultry Supplies", status: "released", totalUsd: 64.98, quantity: 1, ageMinutes: 1200, fulfilment: "Ships from warehouse · UPS", payment: "Neighbor's Club reward" }),
    ],
  },
  {
    id: "660736385", num: 11, name: "Kevin Ross", firstName: "Kevin", lastName: "Ross", email: "kevin.ross@gmail.com", phone: "6155550110",
    zip: "37087", city: "Lebanon", state: "TN", neighborsClub: false,
    orders: [
      order({ orderId: "1410097224", product: "CountyLine Galvanized Steel Hay Feeder", brand: "CountyLine", category: "Livestock Feeders", status: "released", totalUsd: 389.99, quantity: 1, ageMinutes: 2880, fulfilment: "Ships from warehouse · XPO freight", payment: "Credit card" }),
    ],
  },
  {
    id: "834397787", num: 12, name: "Grace Turner", firstName: "Grace", lastName: "Turner", email: "grace.turner@gmail.com", phone: "6155550112",
    zip: "37115", city: "Madison", state: "TN", neighborsClub: false,
    orders: [
      order({ orderId: "1410120872", product: "Fi Series 3 Smart Dog Collar", brand: "Fi", category: "Pet Tech", status: "processing", totalUsd: 119.99, quantity: 1, ageMinutes: 1440, fulfilment: "Ships from vendor · UPS", payment: "Credit card" }),
    ],
  },
  {
    id: "890583835", num: 13, name: "Ben Carter", firstName: "Ben", lastName: "Carter", email: "ben.carter@gmail.com", phone: "6155550113",
    zip: "37130", city: "Murfreesboro", state: "TN", neighborsClub: false,
    orders: [
      order({ orderId: "1410145294", product: "Kaytee Timothy Hay 96 oz", brand: "Kaytee", category: "Small Pet Bedding", status: "processing", totalUsd: 15.49, quantity: 1, ageMinutes: 20, fulfilment: "Ships from vendor · UPS", payment: "Credit card" }),
    ],
  },
];

const slug = (name: string) => name.toLowerCase().replace(/[^a-z]+/g, "-");

export const TSC_CUSTOMERS: TscCustomer[] = PEOPLE.map((p) => ({
  ...p,
  // Newest order first, so the card leads with the most recent one.
  orders: [...p.orders].sort((a, b) => a.ageMinutes - b.ageMinutes),
  photo: `/tsc/customers/${slug(p.name)}.png`,
  loyaltyTier: p.neighborsClub ? "Neighbor's Club" : "",
  ordersCount: p.orders.length,
  crmNotes: "",
}));

export const TSC_PERSONA_ID_PREFIX = "tsc_";

export function getTscCustomer(id: string): TscCustomer | undefined {
  return TSC_CUSTOMERS.find((c) => c.id === id);
}

export function tscPersonaId(c: TscCustomer): string {
  return `${TSC_PERSONA_ID_PREFIX}${c.id}`;
}

/** Seed status → the console's status chip. */
const STATUS: Record<string, { status: OrderStatus; label: string }> = {
  processing: { status: "label_created", label: "Processing" },
  released:   { status: "pickup_processing", label: "Released to warehouse" },
  shipped:    { status: "in_transit", label: "Shipped" },
  delivered:  { status: "delivered", label: "Delivered" },
  cancelled:  { status: "cancelled", label: "Cancelled" },
};

export function tscStatus(raw: string): { status: OrderStatus; label: string } {
  return STATUS[raw] ?? { status: "label_created", label: raw };
}

export function formatTscPhone(digits: string): string {
  const d = digits.replace(/\D/g, "");
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : digits;
}

/** The 9-digit customer ID in threes, the way Riley reads it back: "273 023 690". */
export function formatTscCustomerId(id: string): string {
  const d = id.replace(/\D/g, "");
  return d.length === 9 ? `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6)}` : id;
}

export function tscCustomerToPersona(c: TscCustomer): PersonaConfig {
  return {
    id: tscPersonaId(c),
    name: c.name,
    role: `Customer ${c.num}`,
    avatarInitials: (c.firstName[0] + c.lastName[0]).toUpperCase(),
    avatarBg: "from-neutral-500 to-neutral-700",
    tagLine: `Customer ${c.num}`,
    email: c.email,
    phone: formatTscPhone(c.phone),
    accountId: `Customer ID ${formatTscCustomerId(c.id)}`,  // display only: under the name on the call page
    memberSince: "",
    loyaltyTier: c.loyaltyTier,
    orders: c.orders.map((o): PersonaOrder => {
      const s = tscStatus(o.status);
      return {
        orderId: o.orderId, product: o.product, status: s.status, statusLabel: s.label,
        date: placedLabel(o.ageMinutes), totalUsd: o.totalUsd,
        highlight: isWithinWindow(o) ? "Within 59 min" : undefined,
      };
    }),
    ordersCount: c.orders.length,
    bestFor: [],
    suggestedQuestions: {},
    city: c.city,
    state: c.state,
    postalCode: c.zip,
    photoUrl: c.photo,
    crmNotes: "",
  };
}
